import { useEffect, useMemo, useState, type CSSProperties, type JSX } from 'react';
import { useT, type CopyKey, type T } from '../../i18n/index.js';
import { socket } from '../../net/socket.js';
import { useApp } from '../../state/store.js';
import { GameRoom } from '../parts/GameRoom.js';
import './TongitsBoard.css';

/**
 * S7 — Tongits at the sari-sari bench (design 2b, 4a). Presentation only.
 *
 * Every affordance on this board is read off `view.legal`, which the engine
 * publishes for the seat on the clock alone (packages/engines/src/tongits).
 * Nothing here re-derives a rule, and there are three that are very easy to get
 * wrong from the cards alone:
 *
 *  - the tapon is only takeable when it melds *immediately*, and whether it does
 *    depends on every meld already on the table, not just your hand;
 *  - while you still owe the tapon card a home, a discard is illegal — until
 *    your own plays make that card unusable, at which point it becomes legal
 *    again (the engine's escape hatch);
 *  - tumba needs a meld of yours on the table first.
 *
 * A board that guessed at any of those would offer taps the server rejects, and
 * a rejected tap in a timed turn costs chips.
 *
 * The round plywood surface is described once, in `felt`; the room's camera
 * decides whether you are looking down at it or across it. The bunutan, the
 * tapon and every laid-down bahay lie flat, because that is where they really
 * are. The only thing that stands up is a seat's held cards — nobody puts their
 * hand face-down on the table — and your own held cards are not on the table at
 * all, which is why they go to the near rail instead.
 */

/* ── The view, as the engine publishes it ─────────────────────────────────── */

export type MeldKind = 'set' | 'run';

export interface TongitsMeld {
  id: number;
  owner: number;
  kind: MeldKind;
  cards: string[];
}

export interface TongitsScore {
  seat: number;
  deadwood: number;
  cards: number;
  melded: number;
}

export interface TongitsOverView {
  reason: string;
  winner: number | null;
  caller: number | null;
  callerBusted: boolean;
  quitter: number | null;
  scores: TongitsScore[];
}

/** The engine's own action shapes. Sent back verbatim from the legal list. */
export type TongitsAction =
  | { kind: 'draw'; source: 'stock' | 'discard' }
  | { kind: 'meld'; cards: string[] }
  | { kind: 'sapaw'; meldId: number; card: string }
  | { kind: 'discard'; card: string }
  | { kind: 'tumba' }
  | { kind: 'laban' }
  | { kind: 'atras' };

export interface TongitsView {
  seats: number;
  /** Seat index, or null on the rail. */
  you: number | null;
  hand: string[];
  deadwood: number | null;
  /** Public: how many cards each seat holds. Never which ones. */
  handCounts: number[];
  melds: TongitsMeld[];
  discard: string[];
  discardTop: string | null;
  /** Public count of a private pile — the bunutan's contents are nobody's. */
  stockCount: number;
  turn: number | null;
  phase: 'draw' | 'act' | 'respond';
  /** The open Fight/Fold window, or null. Public — everyone saw it called. */
  fight: { caller: number; laban: number[]; atras: number[] } | null;
  /** The card the seat on the clock still owes a meld for. Public. */
  tookDiscard: string | null;
  legal: TongitsAction[];
  over: TongitsOverView | null;
}

/** A miron arrives with this seat index — no seat, no taps, no "mine". */
const SPECTATOR_SEAT = -1;

/**
 * Where a chair's cards live on the 360×360 plywood, indexed by the chair
 * GameRoom rotated the player into: 0 is the near one, 1 the far left, 2 the
 * far right. Table-local pixels with the middle of the table at (180, 180),
 * lifted off the mock's own staging (design 4a). Tongits seats exactly three
 * (protocol VENUES), which is why there are three of these and no more.
 *
 * `bahay` is the middle of that seat's laid-down melds; `held` is where the
 * bottom edge of their fan of face-down cards rests. Every bahay sits further
 * from the rim than its own held fan, for two reasons that happen to agree: a
 * standing fan hides whatever is behind it, and lying flat in top view it would
 * otherwise hang off the round edge of the table.
 */
const SPOTS: { bahay: [number, number]; held: [number, number] }[] = [
  // The near bahay's anchor is its *bottom* edge (see the CSS), so this is the
  // line your melds grow up from rather than the middle of them.
  { bahay: [180, 296], held: [180, 344] },
  { bahay: [92, 148], held: [96, 112] },
  { bahay: [268, 148], held: [264, 112] },
];

/* ── How a meld fits its bahay ────────────────────────────────────────────── */

/*
 * These mirror `.tng__meld` and `.tng__bahay` in TongitsBoard.css: a 28px card,
 * 9px of it hidden under the next, and 10px of the meld's own padding and
 * border. The near bahay spreads across 236px; the other two stack in 104px
 * columns beside their chairs.
 */
const CARD_W = 28;
const CARD_FAN = 19;
const MELD_CHROME = 10;
export const bahayColumn = (slot: number): number => (slot === 0 ? 236 : 104);

/**
 * How far to shrink a meld so it stays inside its bahay.
 *
 * A meld is not a fixed three cards — sapaw is the move the whole game turns
 * on, and every one of them adds a card to somebody's meld. Four cards is 95px
 * and the opponents' column is 104, so the *fifth* card runs out of the bahay
 * and onto the felt; the bahay is absolutely positioned and clips nothing, so
 * it goes over whatever is beside it.
 *
 * Scaling, rather than fanning tighter: the card's rank sits in a 14px corner
 * and the fan is already only 19px, so there is about three pixels of overlap
 * to be had before "10" starts losing its zero. There is no room to trade, so
 * the meld gets smaller — the same answer the discard pool gets, for the
 * opposite reason.
 */
export function meldScale(cards: number, column: number): number {
  const room = column - MELD_CHROME;
  return Math.min(1, room / (CARD_W + CARD_FAN * Math.max(cards - 1, 0)));
}

/** The bunutan and the tapon, side by side in the middle of the table. */
const STOCK_AT: [number, number] = [152, 186];
const TAPON_AT: [number, number] = [208, 186];

export function TongitsBoard(): JSX.Element {
  const match = useApp((s) => s.match);
  const t = useT();
  const [sel, setSel] = useState<string[]>([]);
  const [asking, setAsking] = useState(false);

  const view = useMemo(() => readTongitsView(match?.view), [match?.view]);

  // Any new server event invalidates a selection — the hand moved under it.
  useEffect(() => {
    setSel([]);
    setAsking(false);
  }, [match?.seq, match?.matchId]);

  if (!match || !view) {
    return <GameRoom felt={<p className="room__wait">{t('dama.tng.wait')}</p>} />;
  }

  const { hand, melds, legal, phase, turn, stockCount, discardTop, tookDiscard, over } = view;
  const mySeat = match.seat > SPECTATOR_SEAT ? match.seat : null;
  const watching = mySeat === null;
  const myTurn = mySeat !== null && turn === mySeat && over === null;

  /* Affordances, all of them straight off the legal list. */
  const canDrawStock = legal.some((a) => a.kind === 'draw' && a.source === 'stock');
  const canDrawDiscard = legal.some((a) => a.kind === 'draw' && a.source === 'discard');
  const meldGroups = legal.flatMap((a) => (a.kind === 'meld' ? [a.cards] : []));
  const sapaws = legal.flatMap((a) => (a.kind === 'sapaw' ? [a] : []));
  const discardable = new Set(legal.flatMap((a) => (a.kind === 'discard' ? [a.card] : [])));
  const canTumba = legal.some((a) => a.kind === 'tumba');
  /**
   * A fight has been called and this seat owes an answer. `legal` is the
   * authority — the engine asks one responder at a time so every wait has a
   * real deadline, so this is true for exactly one seat at a time.
   */
  const answering = legal.some((a) => a.kind === 'laban');
  const fightOpen = view.fight !== null && over === null;

  /** Cards that appear in at least one meld the engine is offering. */
  const inSomeMeld = new Set(meldGroups.flat());
  const sapawCards = new Set(sapaws.map((a) => a.card));
  const picked = new Set(sel);

  const matchedMeld = meldGroups.find((g) => sameCards(g, sel)) ?? null;
  const lone = sel.length === 1 ? (sel[0] ?? null) : null;
  const sapawTargets = new Set(
    lone === null ? [] : sapaws.filter((a) => a.card === lone).map((a) => a.meldId),
  );
  const canDiscard = lone !== null && discardable.has(lone);

  /**
   * Emptying your hand ends the hand on the spot — there is no "Tongits!"
   * action to press, so the call has to be shown on the play that would do it
   * or the player never sees it coming.
   */
  const meldWins = matchedMeld !== null && matchedMeld.length === hand.length;
  const lastCard = hand.length === 1;

  const send = (action: TongitsAction): void => {
    socket.send({ t: 'match.action', matchId: match.matchId, seq: match.seq, action });
    setSel([]);
    setAsking(false);
  };

  const toggle = (card: string): void => {
    if (!myTurn || phase !== 'act') return;
    setSel((cur) => (cur.includes(card) ? cur.filter((c) => c !== card) : [...cur, card]));
  };

  const fight = over !== null && (over.reason === 'tumba' || over.reason === 'exhausted');
  const tongits = over !== null && over.reason === 'tongits';

  /**
   * GameRoom turns the real seat indices so the local player always has the
   * near chair. The felt has to place a seat's cards in that same frame, or a
   * player's bahay would end up sitting in front of somebody else's stool.
   */
  const count = Math.max(view.handCounts.length, 1);
  const slotOf = (seat: number): number => (seat - (mySeat ?? 0) + count) % count;

  const felt = (
    <div className="tng" data-fight={fight}>
      {/* Draw decision: two piles, and only the ones the engine says. */}
      <button
        className="tng__pile"
        data-kind="stock"
        data-fly="stock"
        style={at(STOCK_AT)}
        disabled={!canDrawStock}
        onClick={() => send({ kind: 'draw', source: 'stock' })}
        aria-label={t('dama.tng.stock', { n: stockCount })}
      >
        <span className="tng-baraha tng-baraha--back" aria-hidden="true">
          <span className="tng-baraha__roundel">T</span>
        </span>
      </button>

      <button
        className="tng__pile"
        data-kind="tapon"
        data-fly="pile"
        style={at(TAPON_AT)}
        disabled={!canDrawDiscard}
        onClick={() => send({ kind: 'draw', source: 'discard' })}
        aria-label={
          discardTop === null
            ? t('dama.tng.tapon.empty')
            : t('dama.tng.tapon.take', { card: cardName(t, discardTop) })
        }
      >
        {discardTop === null ? (
          <span className="tng-baraha tng-baraha--empty" aria-hidden="true" />
        ) : (
          <Baraha code={discardTop} />
        )}
      </button>

      {/* Every seat's bahay, laid flat on the plywood by their own chair. */}
      {SPOTS.map((spot, slot) => {
        const laid = melds.filter((m) => slotOf(m.owner) === slot);
        if (laid.length === 0) return null;
        return (
          <div
            className="tng__bahay"
            key={slot}
            data-chair={slot}
            // Seat space, not chair space: the flight layer thinks in seats,
            // and this chair belongs to exactly one of them.
            data-fly={`melds-${(slot + (mySeat ?? 0)) % count}`}
            style={at(spot.bahay)}
          >
            {laid.map((m) => {
              const target = sapawTargets.has(m.id);
              return (
                <button
                  key={m.id}
                  className="tng__meld"
                  data-mine={m.owner === mySeat}
                  data-target={target}
                  // The ghost is a real card's worth of width, so it is counted
                  // — otherwise the meld overflows for exactly as long as the
                  // sapaw preview is showing, which is when it is being read.
                  style={
                    {
                      '--meld-k': meldScale(
                        m.cards.length + (target && lone !== null ? 1 : 0),
                        bahayColumn(slot),
                      ),
                    } as CSSProperties
                  }
                  disabled={!target}
                  onClick={() => lone !== null && send({ kind: 'sapaw', meldId: m.id, card: lone })}
                  aria-label={meldLabel(t, m, mySeat, watching, target)}
                >
                  <span className="tng__meld-cards">
                    {m.cards.map((c) => (
                      <Baraha key={c} code={c} mini />
                    ))}
                    {target && lone !== null ? (
                      <span className="tng__ghost">
                        <Baraha code={lone} mini />
                      </span>
                    ) : null}
                  </span>
                  {/* A sapaw that empties your hand is a Tongits too, and the
                      only place that is visible is the meld being tapped. */}
                  {target ? (
                    <span className="tng__cue">{lastCard ? 'Tongits!' : t('dama.tng.cue.sapaw')}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        );
      })}

      {/*
        The other chairs' cards. They are held, so they stand up — the count on
        the pill is public in the view, the faces never are.
      */}
      {view.handCounts.map((n, seat) => {
        const spot = SPOTS[slotOf(seat)];
        if (seat === mySeat || spot === undefined) return null;
        return (
          <span className="tng__held up" key={seat} style={at(spot.held)}>
            {Array.from({ length: Math.min(n, 3) }, (_, k) => (
              <i key={k} data-k={k} />
            ))}
            <b>{t('dama.tng.cards', { n })}</b>
          </span>
        );
      })}
    </div>
  );

  return (
    <GameRoom
      felt={felt}
      hand={
        <div className="tng-rail" data-live={myTurn && phase === 'act'} data-fight={fight}>
          {watching ? (
            <p className="tng-rail__note">{t('dama.tng.rail.watch')}</p>
          ) : hand.length === 0 ? (
            <p className="tng-rail__note">{t('dama.tng.rail.empty')}</p>
          ) : (
            /* Sorted by the engine; kept in that order so the fan never jumps. */
            <div className="tng-rail__fan">
              {hand.map((c) => (
                <button
                  key={c}
                  className="tng-rail__card"
                  data-picked={picked.has(c)}
                  data-usable={inSomeMeld.has(c) || sapawCards.has(c) || discardable.has(c)}
                  /* The tapon debt lives on the card that owes it, not in a
                     banner — the player is looking at the fan, not the HUD. */
                  data-owed={c === tookDiscard}
                  disabled={!myTurn || phase !== 'act'}
                  onClick={() => toggle(c)}
                  aria-pressed={picked.has(c)}
                  aria-label={
                    c === tookDiscard
                      ? t('dama.tng.card.owed', { card: cardName(t, c) })
                      : cardName(t, c)
                  }
                >
                  <Baraha code={c} />
                </button>
              ))}
            </div>
          )}
        </div>
      }
      toast={
        answering ? (
          <>
            <b>
              {t('dama.tng.fight.head', {
                who: seatName(t, view.fight?.caller ?? null, mySeat, watching),
              })}
            </b>{' '}
            — {t('dama.tng.fight.ask')}
          </>
        ) : fightOpen ? (
          <>
            <b>
              {t('dama.tng.fight.head', {
                who: seatName(t, view.fight?.caller ?? null, mySeat, watching),
              })}
            </b>{' '}
            — {t('dama.tng.fight.wait')}
          </>
        ) : asking ? (
          <>
            <b>{t('dama.tng.act.tumba')}</b> — {t('dama.tng.tumba.warn')}
          </>
        ) : (
          turnLine(t, turn, myTurn, phase, tookDiscard, over)
        )
      }
      actions={
        answering ? (
          <>
            <button
              className="room-act"
              data-tone="primary"
              type="button"
              onClick={() => send({ kind: 'laban' })}
            >
              {t('dama.tng.act.laban')}
            </button>
            <button className="room-act" type="button" onClick={() => send({ kind: 'atras' })}>
              {t('dama.tng.act.atras')}
            </button>
          </>
        ) : myTurn && phase === 'act' ? (
          asking ? (
            <>
              <button className="room-act" data-tone="danger" type="button" onClick={() => send({ kind: 'tumba' })}>
                {t('dama.tng.act.tumba.sure')}
              </button>
              <button className="room-act" type="button" onClick={() => setAsking(false)}>
                {t('dama.tng.act.back')}
              </button>
            </>
          ) : (
            <>
              <button
                className="room-act"
                data-tone="primary"
                type="button"
                disabled={matchedMeld === null}
                onClick={() => matchedMeld && send({ kind: 'meld', cards: matchedMeld })}
              >
                {meldWins ? 'Tongits!' : t('dama.tng.act.meld')}
              </button>
              <button
                className="room-act"
                type="button"
                disabled={!canDiscard}
                onClick={() => lone !== null && send({ kind: 'discard', card: lone })}
              >
                {canDiscard && lastCard ? 'Tongits!' : t('dama.tng.act.discard')}
              </button>
              {canTumba ? (
                <button className="room-act" data-tone="danger" type="button" onClick={() => setAsking(true)}>
                  {t('dama.tng.act.tumba')}
                </button>
              ) : null}
              {sel.length > 0 ? (
                <button className="room-act" type="button" onClick={() => setSel([])}>
                  {t('dama.tng.act.clear')}
                </button>
              ) : null}
            </>
          )
        ) : null
      }
      aside={
        <>
          {/*
            The counts are the only reading of the hidden state anywhere on
            screen — how much bunutan is left decides whether the hand can even
            reach a bilangan, and your own deadwood is the number the tumba
            decision turns on. Both are published in the view; neither is on a
            card anybody can see, so they get the HUD rather than the felt.
          */}
          <div className="tng-counts">
            <span>
              <i>{t('dama.tng.count.stock')}</i>
              <b>{stockCount}</b>
            </span>
            <span>
              <i>{t('dama.tng.count.discard')}</i>
              <b>{view.discard.length}</b>
            </span>
            {mySeat !== null && view.deadwood !== null ? (
              <span data-you="true">
                <i>{t('dama.tng.count.points')}</i>
                <b>{view.deadwood}</b>
              </span>
            ) : null}
          </div>

          {/*
           * The signature moment (§G2). It is the one overlay allowed to cover
           * the table, because by the time it runs the hand is over and there is
           * nothing left to read underneath — the seats reveal one after
           * another, and the lamps come up over a table nobody can touch.
           */}
          {fight && over ? (
            <div className="fight room__inert" role="status">
              <div className="fight__lamp" aria-hidden="true" />
              <div className="fight__card">
                <p className="fight__title">
                  {over.reason === 'tumba'
                    ? t('dama.tng.end.laban')
                    : t('dama.tng.end.exhausted')}
                </p>
                <p className="fight__sub">
                  {over.reason === 'tumba'
                    ? t('dama.tng.end.called', {
                        who: seatName(t, over.caller ?? 0, mySeat, watching),
                      })
                    : t('dama.tng.end.nodraw')}
                </p>
                <ol className="fight__rows">
                  {[...over.scores]
                    .sort((a, b) => a.seat - b.seat)
                    .map((sc, i) => (
                      <li
                        key={sc.seat}
                        className="fight__row"
                        style={{ animationDelay: `${i * 260}ms` }}
                        data-win={over.winner === sc.seat}
                        data-burned={over.callerBusted && over.caller === sc.seat}
                      >
                        <span className="fight__who">{seatName(t, sc.seat, mySeat, watching)}</span>
                        <span className="fight__pts data">{sc.deadwood}</span>
                        <span className="fight__note">
                          {over.winner === sc.seat
                            ? t('dama.tng.end.won')
                            : over.callerBusted && over.caller === sc.seat
                              ? t('dama.tng.end.burned')
                              : t('dama.tng.cards', { n: sc.cards })}
                        </span>
                      </li>
                    ))}
                </ol>
                {over.winner === null ? (
                  <p className="fight__sub">{t('dama.tng.end.tabla')}</p>
                ) : null}
              </div>
            </div>
          ) : null}

          {tongits && over ? (
            <div className="fight room__inert" data-instant="true" role="status">
              <div className="fight__card">
                <p className="fight__title">Tongits!</p>
                <p className="fight__sub">
                  {t('dama.tng.end.tongits', {
                    who: seatName(t, over.winner ?? 0, mySeat, watching),
                  })}
                </p>
              </div>
            </div>
          ) : null}
        </>
      }
    />
  );
}

/** Table-local placement, in the plain pixels the felt's box is measured in. */
function at([x, y]: [number, number]): CSSProperties {
  return { '--x': `${x}px`, '--y': `${y}px` } as CSSProperties;
}

/* ── The card ─────────────────────────────────────────────────────────────── */

/**
 * Plastic-coated face, banig-weave back (component sheet 1d). Four-colour suits:
 * the glyph carries the read on its own, and the hue is a second, redundant cue
 * — never the only one (§7.3).
 *
 * `mini` is the meld card from the mock: rank over glyph, centred, because at 28
 * pixels a corner index and an empty middle waste the only space there is.
 *
 * Duplicated in PusoyDosBoard rather than shared: the two boards are the only
 * consumers today and neither owns components/. It belongs in a primitive the
 * moment a third baraha venue lands.
 */
function Baraha({ code, mini = false }: { code: string; mini?: boolean }): JSX.Element {
  const rank = code.slice(0, -1);
  const suit = code.slice(-1);
  const glyph = SUIT_GLYPH[suit] ?? '?';
  if (mini) {
    return (
      <span className="tng-baraha" data-suit={suit} data-mini="true" aria-hidden="true">
        <b>{rank}</b>
        <i>{glyph}</i>
      </span>
    );
  }
  return (
    <span className="tng-baraha" data-suit={suit} aria-hidden="true">
      <span className="tng-baraha__idx">
        <b>{rank}</b>
        <i>{glyph}</i>
      </span>
      <span className="tng-baraha__mid">{glyph}</span>
    </span>
  );
}

const SUIT_GLYPH: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
const SUIT_NAME: Record<string, CopyKey> = {
  S: 'dama.tng.suit.S',
  H: 'dama.tng.suit.H',
  D: 'dama.tng.suit.D',
  C: 'dama.tng.suit.C',
};

function cardName(t: T, code: string): string {
  const suit = SUIT_NAME[code.slice(-1)];
  return t('dama.tng.card', { rank: code.slice(0, -1), suit: suit ? t(suit) : '?' });
}

/* ── Reading the engine's view ────────────────────────────────────────────── */

/**
 * The view arrives as `unknown` (the wire type must not know the rules), so it
 * is narrowed rather than cast. A malformed payload renders the waiting state
 * instead of throwing inside a live match.
 *
 * `legal` is filtered, not trusted wholesale: anything this reader does not
 * recognise is dropped, so a newer server can add an action without this build
 * putting an unpressable button on the table.
 */
export function readTongitsView(raw: unknown): TongitsView | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const v = raw as Record<string, unknown>;
  if (!Array.isArray(v['handCounts'])) return null;

  const hand = cardList(v['hand']);
  const discard = cardList(v['discard']);
  const phase =
    v['phase'] === 'act' ? 'act' : v['phase'] === 'respond' ? 'respond' : 'draw';

  return {
    seats: isNum(v['seats']) ? v['seats'] : v['handCounts'].length,
    you: isNum(v['you']) ? v['you'] : null,
    hand,
    deadwood: isNum(v['deadwood']) ? v['deadwood'] : null,
    handCounts: v['handCounts'].filter(isNum),
    melds: Array.isArray(v['melds'])
      ? v['melds'].map(readMeld).filter((m): m is TongitsMeld => m !== null)
      : [],
    discard,
    discardTop: isCard(v['discardTop']) ? v['discardTop'] : (discard[discard.length - 1] ?? null),
    stockCount: isNum(v['stockCount']) ? v['stockCount'] : 0,
    turn: isNum(v['turn']) ? v['turn'] : null,
    phase,
    tookDiscard: isCard(v['tookDiscard']) ? v['tookDiscard'] : null,
    legal: Array.isArray(v['legal'])
      ? v['legal'].map(readAction).filter((a): a is TongitsAction => a !== null)
      : [],
    fight: readFight(v['fight']),
    over: readOver(v['over']),
  };
}

function readMeld(raw: unknown): TongitsMeld | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const m = raw as Record<string, unknown>;
  if (!isNum(m['id']) || !isNum(m['owner'])) return null;
  const cards = cardList(m['cards']);
  if (cards.length === 0) return null;
  return { id: m['id'], owner: m['owner'], kind: m['kind'] === 'run' ? 'run' : 'set', cards };
}

/** The open Fight/Fold window. Public — nothing in it is anybody's secret. */
function readFight(raw: unknown): TongitsView['fight'] {
  if (typeof raw !== 'object' || raw === null) return null;
  const f = raw as Record<string, unknown>;
  if (!isNum(f['caller'])) return null;
  const seats = (x: unknown): number[] => (Array.isArray(x) ? x.filter(isNum) : []);
  return { caller: f['caller'], laban: seats(f['laban']), atras: seats(f['atras']) };
}

function readAction(raw: unknown): TongitsAction | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const a = raw as Record<string, unknown>;
  switch (a['kind']) {
    case 'draw':
      return a['source'] === 'stock' || a['source'] === 'discard'
        ? { kind: 'draw', source: a['source'] }
        : null;
    case 'meld': {
      const cards = cardList(a['cards']);
      return cards.length > 0 ? { kind: 'meld', cards } : null;
    }
    case 'sapaw':
      return isNum(a['meldId']) && isCard(a['card'])
        ? { kind: 'sapaw', meldId: a['meldId'], card: a['card'] }
        : null;
    case 'discard':
      return isCard(a['card']) ? { kind: 'discard', card: a['card'] } : null;
    case 'tumba':
      return { kind: 'tumba' };
    case 'laban':
      return { kind: 'laban' };
    case 'atras':
      return { kind: 'atras' };
    default:
      return null;
  }
}

function readOver(raw: unknown): TongitsOverView | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const scores = Array.isArray(o['scores'])
    ? o['scores'].flatMap((s): TongitsScore[] => {
        if (typeof s !== 'object' || s === null) return [];
        const sc = s as Record<string, unknown>;
        if (!isNum(sc['seat'])) return [];
        return [
          {
            seat: sc['seat'],
            deadwood: isNum(sc['deadwood']) ? sc['deadwood'] : 0,
            cards: isNum(sc['cards']) ? sc['cards'] : 0,
            melded: isNum(sc['melded']) ? sc['melded'] : 0,
          },
        ];
      })
    : [];
  return {
    reason: typeof o['reason'] === 'string' ? o['reason'] : 'tumba',
    winner: isNum(o['winner']) ? o['winner'] : null,
    caller: isNum(o['caller']) ? o['caller'] : null,
    callerBusted: o['callerBusted'] === true,
    quitter: isNum(o['quitter']) ? o['quitter'] : null,
    scores,
  };
}

const isNum = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
const isCard = (x: unknown): x is string => typeof x === 'string' && x.length >= 2;
const cardList = (x: unknown): string[] => (Array.isArray(x) ? x.filter(isCard) : []);

/** Order-insensitive: the engine's meld groups and a player's taps rarely agree. */
function sameCards(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((c, i) => right[i] === c);
}

function seatName(t: T, seat: number | null, mySeat: number | null, watching: boolean): string {
  if (seat === null) return t('dama.tng.someone');
  if (!watching && seat === mySeat) return t('dama.tng.you');
  return t('dama.seat', { n: seat + 1 });
}

function meldLabel(
  t: T,
  m: TongitsMeld,
  mySeat: number | null,
  watching: boolean,
  target: boolean,
): string {
  const kind = t(m.kind === 'set' ? 'dama.tng.kind.set' : 'dama.tng.kind.run');
  if (!watching && m.owner === mySeat) {
    return t(target ? 'dama.tng.meld.mine.top' : 'dama.tng.meld.mine', { kind });
  }
  return t(target ? 'dama.tng.meld.seat.top' : 'dama.tng.meld.seat', { kind, n: m.owner + 1 });
}

/**
 * One line, not two. The room's player puck already carries whose turn it is and
 * the clock, so the toast's job is the part the turn ring cannot say: which of
 * the two phases this seat is in.
 */
function turnLine(
  t: T,
  turn: number | null,
  myTurn: boolean,
  phase: 'draw' | 'act' | 'respond',
  owed: string | null,
  over: TongitsOverView | null,
): string {
  if (over !== null) return t('dama.tng.turn.over');
  if (turn === null) return t('dama.tng.turn.wait');
  if (!myTurn) return t('dama.tng.turn.seat', { n: turn + 1 });
  if (phase === 'draw') return t('dama.tng.turn.draw');
  // The card itself is ringed and tabbed in the fan; this is the same fact in
  // words, which is why it does not need to name the card again.
  if (owed !== null) return t('dama.tng.turn.owed');
  return t('dama.tng.turn.act');
}
